import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Facebook, Instagram } from 'lucide-react';
import OscarButton from './OscarButton';

const DARK_BLUE_ENVELOPE = 'https://customer-assets.emergentagent.com/job_96f73dc0-b292-41dd-a366-8b41de2ebdcc/artifacts/dkrpi9j5_blue%20envelope%20no%20BG.png';

const GOLD        = '#D4A853';          // bright gold — readable on black
const GOLD_MED    = '#B8943E';          // medium gold for secondary
const GOLD_FAINT  = 'rgba(212,168,83,0.45)'; // hairlines only

const FooterSection = ({ scrollTo }) => {
  const navigate = useNavigate();

  return (
    <footer style={{
      background: '#0D0B08',
      padding: '28px 24px 20px',
      width: '100vw',
      marginLeft: 'calc(-50vw + 50%)',
      textAlign: 'center',
      position: 'relative',
      boxSizing: 'border-box',
    }}>

      {/* Top gold hairline */}
      <div style={{
        width: '50%',
        maxWidth: 180,
        height: 1,
        margin: '0 auto 20px',
        background: `linear-gradient(90deg, transparent, ${GOLD_MED}, transparent)`,
      }} />

      {/* Main heading — Cormorant Garamond italic */}
      <h2 style={{
        fontFamily: "'Cormorant Garamond', serif",
        fontSize: 'clamp(28px, 4vw, 44px)',
        fontWeight: 500,
        fontStyle: 'italic',
        letterSpacing: '1px',
        margin: '0 0 8px',
        color: GOLD,
        lineHeight: 1.2,
      }}>
        80 Years of Connection
      </h2>

      {/* Legacy line */}
      <p style={{
        fontFamily: "'Cinzel', serif",
        fontSize: 11,
        fontWeight: 400,
        color: GOLD_MED,
        letterSpacing: '3px',
        textTransform: 'uppercase',
        margin: '0 0 18px',
      }}>
        A Legacy Est. 1946
      </p>

      {/* Social icons */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 16,
        marginBottom: 18,
      }}>
        <Facebook  size={18} strokeWidth={1.5} style={{ color: GOLD_MED }}  />
        <span style={{ color: GOLD_FAINT, fontSize: 8 }}>•</span>
        <Instagram size={18} strokeWidth={1.5} style={{ color: GOLD_MED }} />
      </div>

      {/* Logo — dark blue M-Vitations envelope */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        marginBottom: 4,
      }}>
        <img
          src={DARK_BLUE_ENVELOPE}
          alt="M-Vitations"
          style={{ width: 160, height: 'auto', objectFit: 'contain', opacity: 0.92 }}
        />
      </div>

      {/* Gold hairline before links */}
      <div style={{
        width: '40%',
        maxWidth: 140,
        height: 1,
        margin: '0 auto 14px',
        background: `linear-gradient(90deg, transparent, ${GOLD_FAINT}, transparent)`,
      }} />

      {/* Brand line — M-Vitations (no parent URL defined; link destination required before activation) */}
      <p style={{
        fontFamily: "'Cormorant Garamond', serif",
        fontSize: 13,
        fontStyle: 'italic',
        color: 'rgba(212,168,83,0.90)',
        margin: '0 0 12px',
        letterSpacing: '0.3px',
      }}>
        Curate your next milestone with{' '}
        <span style={{ color: 'rgba(212,168,83,1.0)' }}>M-Vitations</span>.
      </p>

      {/* Utility links row */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexWrap: 'wrap',
        gap: 8,
        marginBottom: 12,
      }}>
        {[
          { label: 'Privacy Policy', action: () => navigate('/privacy'), testid: 'footer-privacy-link' },
          { label: 'Terms',          action: () => navigate('/terms'),   testid: 'footer-terms-link'   },
          { label: 'Admin',          action: () => { window.location.href = '/admin'; }, testid: 'footer-admin-link' },
        ].map((item, i, arr) => (
          <React.Fragment key={item.testid}>
            <span
              data-testid={item.testid}
              onClick={item.action}
              style={{
                fontFamily: "'Cinzel', serif",
                fontSize: 11,
                letterSpacing: '1.5px',
                textTransform: 'uppercase',
                color: GOLD_MED,
                cursor: 'pointer',
                transition: 'color 0.2s',
              }}
              onMouseEnter={e => e.target.style.color = GOLD}
              onMouseLeave={e => e.target.style.color = GOLD_MED}
            >
              {item.label}
            </span>
            {i < arr.length - 1 && (
              <span style={{ color: GOLD_FAINT, fontSize: 8 }}>•</span>
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Oscar — absolutely positioned right side, zero height contribution */}
      <div style={{
        position: 'absolute',
        right: 16,
        top: '50%',
        transform: 'translateY(-50%)',
        zIndex: 10,
      }}>
        <OscarButton embedded />
      </div>

      {/* Copyright */}
      <p style={{
        fontFamily: "'Cinzel', serif",
        fontSize: 10,
        color: 'rgba(212,168,83,0.85)',
        letterSpacing: '1px',
        textTransform: 'uppercase',
        margin: 0,
      }}>
        © 2026 M-Vitations · All Rights Reserved
      </p>

    </footer>
  );
};

export default FooterSection;
